import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Mail, Check, Loader2 } from "lucide-react";
import { subscribeNewsletter } from "@/lib/newsletter.functions";
import { track } from "@/lib/analytics";
import { cn } from "@/lib/utils";

interface BrevetSignupProps {
  location: string;
  variant?: "block" | "inline";
  className?: string;
}

export function BrevetSignup({ location, variant = "block", className }: BrevetSignupProps) {
  const subscribe = useServerFn(subscribeNewsletter);
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "ok" | "error">("idle");
  const [message, setMessage] = useState("");

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || state === "loading") return;
    setState("loading");
    try {
      const { getAttribSnapshot } = await import("@/lib/analytics/track");
      const attrib = getAttribSnapshot();
      const res = await subscribe({
        data: {
          email,
          source_path: typeof window !== "undefined" ? window.location.pathname : null,
          funnel_id: `brevet:${location}`,
          user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
          utm_source: attrib.utm_source,
          utm_medium: attrib.utm_medium,
          utm_campaign: attrib.utm_campaign,
          session_id: attrib.session_id,
          visitor_id: attrib.visitor_id,
        },
      });
      if (res.ok) {
        setState("ok");
        setMessage(res.alreadySubscribed ? "Du är redan med på listan ✓" : "Tack! Du är med — första Brevet landar nästa fredag.");
        track("newsletter_subscribe", { location, already: res.alreadySubscribed ? 1 : 0 });
      } else {
        setState("error");
        setMessage("Något gick fel. Prova igen om en stund.");
      }
    } catch {
      setState("error");
      setMessage("Något gick fel. Prova igen om en stund.");
    }
  }

  if (variant === "inline") {
    return (
      <div className={cn("rounded-2xl border border-white/10 bg-surface/60 p-5 md:p-6", className)}>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-mint">
          <Mail className="h-3.5 w-3.5" /> Brevet
        </div>
        <p className="mt-2 text-sm text-foreground/85">
          En kort månadsdos: ett case, en trend, ett verktyg. Inga utskick mellan.
        </p>
        <Form
          email={email}
          setEmail={setEmail}
          onSubmit={onSubmit}
          state={state}
          message={message}
          compact
        />
      </div>
    );
  }

  return (
    <div className={cn("rounded-3xl border border-mint/20 bg-gradient-to-br from-mint/[0.08] via-surface/40 to-butter/[0.05] p-8 md:p-12", className)}>
      <div className="grid gap-8 md:grid-cols-[1.2fr_1fr] md:items-center">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-mint">
            <Mail className="h-3.5 w-3.5" /> Brevet — månadsbrev från Fenny
          </div>
          <h3 className="mt-4 text-balance text-3xl font-semibold leading-tight md:text-4xl">
            Det enda nyhetsbrev om film du faktiskt vill öppna.
          </h3>
          <p className="mt-4 max-w-lg text-pretty text-base text-muted-foreground md:text-lg">
            Ett case som överraskade oss. En trend vi följer. Ett verktyg vi själva använder.
            Skickas en gång i månaden — aldrig oftare.
          </p>
          <ul className="mt-5 grid gap-2 text-sm text-foreground/80">
            <li className="flex items-center gap-2"><Check className="h-4 w-4 text-mint" /> 4 min läsning</li>
            <li className="flex items-center gap-2"><Check className="h-4 w-4 text-mint" /> Inga säljutskick mellan</li>
            <li className="flex items-center gap-2"><Check className="h-4 w-4 text-mint" /> Avsluta när du vill</li>
          </ul>
        </div>
        <Form email={email} setEmail={setEmail} onSubmit={onSubmit} state={state} message={message} />
      </div>
    </div>
  );
}

function Form({
  email, setEmail, onSubmit, state, message, compact,
}: {
  email: string;
  setEmail: (v: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  state: "idle" | "loading" | "ok" | "error";
  message: string;
  compact?: boolean;
}) {
  if (state === "ok") {
    return (
      <div className={cn("flex items-start gap-3 rounded-2xl border border-mint/40 bg-mint/10 p-4 text-sm text-foreground/90", compact && "mt-4")}>
        <Check className="mt-0.5 h-5 w-5 shrink-0 text-mint" />
        <span>{message}</span>
      </div>
    );
  }
  return (
    <form data-track-form="nyhetsbrev" onSubmit={onSubmit} className={cn("space-y-3", compact && "mt-4")}>
      <label className="block">
        <span className="sr-only">Din e-post</span>
        <input
          type="email"
          required
          autoComplete="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="namn@företag.se"
          className="w-full rounded-full border border-white/15 bg-background/80 px-5 py-3 text-sm placeholder:text-muted-foreground focus:border-mint focus:outline-none focus:ring-2 focus:ring-mint/30"
          disabled={state === "loading"}
        />
      </label>
      <button
        type="submit"
        disabled={state === "loading"}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-mint px-5 py-3 text-sm font-medium text-mint-foreground transition-transform hover:-translate-y-0.5 disabled:opacity-60"
      >
        {state === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
        {state === "loading" ? "Anmäler…" : "Få Brevet varje månad"}
      </button>
      {state === "error" && (
        <p className="text-xs text-peach">{message}</p>
      )}
      <p className="text-[11px] text-muted-foreground">
        Vi sparar bara din e-post för att skicka Brevet. Aldrig delning, aldrig spam.
      </p>
    </form>
  );
}
